///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, HostListener, OnInit } from '@angular/core';
import { GtthPreferencesService } from './gtth-preferences.service';
import { GtthAlertService } from '../services/alert.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { LoaderIds, PreloaderService } from '../services/preloader.service';
import { finalize } from 'rxjs';
import { SuiTopologyPreferencesDTO } from '../generated/shared-sui-types.model';

@Component({
    selector: 'winde-preferences-edit-dialog',
    templateUrl: './preferences-edit-dialog.component.html',
    providers: [GtthPreferencesService],
    standalone: false
})
export class PreferencesEditDialogComponent implements OnInit {
    public preferences: SuiTopologyPreferencesDTO;
    public loaderKey = LoaderIds.STYLE_PREFERENCES_DIALOG_PRELOADER_ID;

    constructor(
        private readonly alertService: GtthAlertService,
        private readonly preferencesService: GtthPreferencesService,
        public readonly preloaderService: PreloaderService,
        public dialogRef: MatDialogRef<PreferencesEditDialogComponent>
    ) {}

    ngOnInit() {
        this.preloaderService.show(this.loaderKey);

        this.preferencesService
            .getPreferences()
            .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
            .subscribe((res: SuiTopologyPreferencesDTO) => {
                this.initPreferences(res);
            });
    }

    save() {
        if (!this.areAllControlsValidity()) {
            return;
        }
        this.preloaderService.show(this.loaderKey);
        this.preferencesService
            .createPreferences(this.preferences)
            .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
            .subscribe({
                next: (_res: any) => this.onSaveSuccess(),
                error: (res: Response) => this.onSaveError(res)
            });
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyboardUpEvent(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            this.save();
        }
    }

    private onSaveError(error) {
        try {
            error.json();
        } catch (exception) {
            error.message = error.text();
        }
        this.onError(error);
    }

    private onError(error) {
        this.alertService.error(error.message, null, null);
    }

    private onSaveSuccess() {
        this.dialogRef.close(this.preferences);
    }

    private initPreferences(topologyPreferencesDTO: SuiTopologyPreferencesDTO): void {
        this.preferences = topologyPreferencesDTO;
        for (const key of Object.keys(topologyPreferencesDTO)) {
            topologyPreferencesDTO[key].isMandatory = true;
            topologyPreferencesDTO[key].validationMessageParams.isActivated = true;
            topologyPreferencesDTO[key].validationMessageParams.isEnabled = true;
        }
    }

    private areAllControlsValidity(): boolean {
        return this.preferences ? Object.values(this.preferences).every((control) => control.isValid) : true;
    }
}

@Component({
    selector: 'winde-preferences-edit-popup',
    template: '',
    standalone: false
})
export class GtthPreferencesEditDialogPopupComponent implements OnInit {
    constructor(
        private readonly router: Router,
        private readonly dialog: MatDialog
    ) {}

    ngOnInit() {
        const dialogRef = this.dialog.open(PreferencesEditDialogComponent, {
            width: '800px',
            position: {
                top: '30px'
            },
            closeOnNavigation: true
        });

        dialogRef.afterClosed().subscribe(() => {
            this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
        });
    }
}
